/**
 * LinkEditorPopover — small floating form that replaces `window.prompt`
 * for every link-author entry point in the editor. Anchored to the
 * trigger (toolbar button, BubbleMenu, or clicked `<a>`) via a
 * `DOMRect`, portals to `document.body`, dismisses on Esc + outside
 * click. Matches the shape of `EmojiPickerPopover` for consistency with
 * the chat-side patterns.
 *
 * Every link entry point (toolbar button, `/link` slash command,
 * link-click edit) routes through this same popover with both URL and
 * Text fields visible — `showTextField` is `true` for the unified flow.
 * The legacy "toolbar uses URL-only popover + extendMarkRange().setLink"
 * path was retired so users get a consistent insert-at-range author
 * experience regardless of how they reached the popover.
 *
 * Affordances by state:
 *   - **New link** (cursor not in an existing link mark): URL field +
 *     Text field + "Add link" primary action. Text field prefills with
 *     the visible text of the active selection (if any). Submit
 *     inserts `<a href={url}>{text}</a>` at the captured range, falling
 *     back to the URL as visible text when the Text field is left blank.
 *   - **Editing existing link** (link clicked in editor OR toolbar
 *     button pressed while caret is inside a link mark): URL field
 *     prefilled with the current `href` AND Text field prefilled with
 *     the link's visible label. Both are editable — submitting replaces
 *     the link's text + href atomically via the same insert-at-range
 *     path. Primary action is "Update", plus "Open" (new tab) and
 *     "Unlink".
 *
 * URL normalization runs at submit time via `normalize-link-url.ts` —
 * bare hostnames (`example.com`) auto-prepend `https://`, non-http(s)
 * schemes are rejected with the same inline-error message as a fully
 * malformed string. Empty / whitespace-only input → null → inline error
 * ("enter a valid URL"); the user can also Unlink instead of clearing.
 *
 * Keyboard:
 *   - Auto-focus the URL field on open.
 *   - Enter submits (from either field).
 *   - Tab moves URL → Text → primary action.
 *   - Esc closes without applying any change.
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { getTabbables } from '@/lib/tabbable'

import { normalizeLinkUrl } from './lib/normalize-link-url'

interface LinkEditorPopoverProps {
  open: boolean
  anchorRect: DOMRect | null
  /** Existing `href` when editing — `null` for the add-new path. */
  initialUrl: string | null
  /**
   * Existing visible text of the selection at trigger time. When
   * `showTextField` is true and this is non-empty the Text field is
   * prefilled with this string. Ignored when `showTextField` is false.
   */
  initialText?: string
  /**
   * When true, render the Text field above the buttons row. Defaults to
   * the unified flow today — every link entry point in the editor
   * (toolbar button, `/link` slash command, link-click edit) opens the
   * popover with the Text field visible so URL + visible label are
   * authored together and submit follows the insert-at-range path. The
   * prop stays here as an explicit override for future surfaces that
   * deliberately want URL-only (e.g. a follow-up "edit href only"
   * affordance) — no in-tree caller currently passes `false`.
   */
  showTextField?: boolean
  /**
   * Submit callback. `text` is `undefined` when the user left the
   * field blank AND there was no `initialText` to fall back to; in
   * that case the caller substitutes the URL as the visible text.
   */
  onSubmit: (payload: { url: string; text?: string }) => void
  /** Provided only in the editing path. Calling it should `unsetLink()` upstream. */
  onUnlink?: () => void
  onClose: () => void
}

const POPOVER_WIDTH = 320

function resolveAnchor(rect: DOMRect, height: number): { top: number; left: number } {
  const vw = window.innerWidth
  const vh = window.innerHeight
  // Prefer BELOW the trigger so the popover floats over the editor
  // surface (locally-owned, calm UI) instead of the chat timeline
  // above the compact-toolbar variant. Flip above only when there's
  // not enough viewport room beneath the trigger.
  const fitsBelow = vh - rect.bottom >= height + 8
  const top = fitsBelow ? rect.bottom + 8 : Math.max(8, rect.top - height - 8)
  const desiredLeft = rect.left
  const left = Math.max(8, Math.min(vw - POPOVER_WIDTH - 8, desiredLeft))
  return { top, left }
}

export function LinkEditorPopover({
  open,
  anchorRect,
  initialUrl,
  initialText,
  showTextField = false,
  onSubmit,
  onUnlink,
  onClose,
}: LinkEditorPopoverProps) {
  const { t } = useTranslation('common')
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [value, setValue] = useState(initialUrl ?? '')
  const [text, setText] = useState(initialText ?? '')
  const [error, setError] = useState<string | null>(null)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)

  // Reset draft + error each time the popover opens against a new anchor.
  useEffect(() => {
    if (!open) return
    setValue(initialUrl ?? '')
    setText(initialText ?? '')
    setError(null)
  }, [open, initialUrl, initialText])

  useEffect(() => {
    if (!open || !anchorRect) {
      setCoords(null)
      return
    }
    // Estimated card height — two buttons + input + error slot. The actual
    // rendered height is close enough to this that flip-below correctness
    // is preserved at every realistic viewport.
    setCoords(resolveAnchor(anchorRect, 180))
  }, [open, anchorRect])

  useEffect(() => {
    if (!open) return
    // Focus the URL field on next tick so the popover render lands in the
    // DOM before we move focus into it. `requestAnimationFrame` is more
    // reliable than a 0ms setTimeout under React 18 strict-mode rerenders.
    const raf = requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    })
    return () => cancelAnimationFrame(raf)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (popoverRef.current?.contains(target)) return
      onClose()
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open, onClose])

  if (!open || !coords) return null

  const handleSubmit = () => {
    const normalized = normalizeLinkUrl(value)
    if (normalized === null) {
      setError(t('editor.linkPopover.invalid'))
      inputRef.current?.focus()
      return
    }
    // Text field is part of the unified author flow (`showTextField`
    // is `true` for every in-tree caller today). Empty input → no
    // text override; the caller substitutes the URL as the visible
    // text. Guarded on `showTextField` so the prop's "URL-only"
    // override still works for any future surface that deliberately
    // opts out.
    const trimmedText = text.trim()
    onSubmit({
      url: normalized,
      text: showTextField && trimmedText.length > 0 ? trimmedText : undefined,
    })
    onClose()
  }

  const handleOpenLink = () => {
    if (!initialUrl) return
    window.open(initialUrl, '_blank', 'noopener,noreferrer')
  }

  const isEditing = initialUrl !== null

  return createPortal(
    <div
      ref={popoverRef}
      // biome-ignore lint/a11y/useSemanticElements: popover, not a true modal — same rationale as EmojiPickerPopover.
      role="dialog"
      aria-label={isEditing ? t('editor.linkPopover.titleEdit') : t('editor.linkPopover.titleAdd')}
      className="fixed z-50 rounded-md border border-border bg-bg-elevated p-3 shadow-2xl ring-1 ring-black/5"
      onKeyDown={(event) => {
        // The popover is portalled to <body>, so Tab off either end has
        // nowhere in this subtree to go and leaves the document flow (inside
        // a modal, that is the page behind it). Close and let the caller
        // return focus to the editor instead.
        if (event.key !== 'Tab') return
        const tabbables = getTabbables(event.currentTarget)
        const edge = event.shiftKey ? tabbables[0] : tabbables[tabbables.length - 1]
        if (document.activeElement === edge) {
          event.preventDefault()
          onClose()
        }
      }}
      style={{
        top: coords.top,
        left: coords.left,
        width: POPOVER_WIDTH,
      }}
    >
      <label htmlFor="editor-link-popover-url" className="mb-1 block text-xs font-medium text-fg">
        {t('editor.linkPopover.urlLabel')}
      </label>
      <input
        ref={inputRef}
        id="editor-link-popover-url"
        type="url"
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          if (error) setError(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleSubmit()
          }
        }}
        placeholder={t('editor.linkPopover.urlPlaceholder')}
        aria-invalid={error !== null}
        aria-describedby={error ? 'editor-link-popover-error' : undefined}
        className="w-full rounded-sm border border-border bg-bg px-2 py-1 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-accent"
      />
      {showTextField && (
        <>
          <label
            htmlFor="editor-link-popover-text"
            className="mt-2 mb-1 block text-xs font-medium text-fg"
          >
            {t('editor.linkPopover.textLabel')}
          </label>
          <input
            id="editor-link-popover-text"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSubmit()
              }
            }}
            placeholder={t('editor.linkPopover.textPlaceholder')}
            className="w-full rounded-sm border border-border bg-bg px-2 py-1 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </>
      )}
      {error && (
        <p id="editor-link-popover-error" role="alert" className="mt-1 text-xs text-danger">
          {error}
        </p>
      )}
      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {isEditing && (
            <>
              <button
                type="button"
                onClick={handleOpenLink}
                className="rounded-sm px-2 py-1 text-xs text-fg-muted hover:bg-bg-muted hover:text-fg focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {t('editor.linkPopover.open')}
              </button>
              <button
                type="button"
                onClick={() => {
                  onUnlink?.()
                  onClose()
                }}
                className="rounded-sm px-2 py-1 text-xs text-fg-muted hover:bg-bg-muted hover:text-fg focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {t('editor.linkPopover.unlink')}
              </button>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-sm bg-accent px-3 py-1 text-xs font-medium text-accent-fg hover:opacity-90 active:opacity-80 focus:outline-none focus:ring-2 focus:ring-accent"
        >
          {isEditing ? t('editor.linkPopover.submitUpdate') : t('editor.linkPopover.submitAdd')}
        </button>
      </div>
    </div>,
    document.body,
  )
}
